/**
 * Subject Weekly Topics Configuration
 * Maps teacher departments to their curriculum weekly data
 * Used by the Batch Generator to show relevant topics per teacher
 */

import { biologyWeeklyLessons } from '../../data/biologyWeeklyLessons';
import { mathematicsWeeklyLessons } from '../../data/mathematicsWeeklyLessons';
import { chemistryWeeklyLessons } from '../../data/chemistryWeeklyLessons';
import { physicsWeeklyLessons } from '../../data/physicsWeeklyLessons';
import { agricultureWeeklyLessons } from '../../data/agricultureWeeklyLessons';
import { economicsWeeklyLessons } from '../../data/economicsWeeklyLessons';
import { accountingWeeklyLessons } from '../../data/accountingWeeklyLessons';
import { geographyWeeklyLessons } from '../../data/geographyWeeklyLessons';
import { developmentStudiesWeeklyLessons } from '../../data/developmentStudiesWeeklyLessons';
import { eslWeeklyLessons } from '../../data/eslWeeklyLessons';
import { frenchWeeklyLessons } from '../../data/frenchWeeklyLessons';
import { seniorFrenchWeeklyLessons } from '../../data/seniorFrenchWeeklyLessons';
import { historyWeeklyLessons } from '../../data/historyWeeklyLessons';
import { juniorMathematicsWeeklyLessons } from '../../data/juniorMathematicsWeeklyLessons';
import { juniorScienceWeeklyLessons } from '../../data/juniorScienceWeeklyLessons';
import { year11ICTWeeklyLessons, year12ICTWeeklyLessons, year13ICTWeeklyLessons } from '../../data/ictWeeklyLessons';
import { frenchMathematicsWeeklyLessons } from '../../data/frenchMathematicsWeeklyLessons';
import { eflWeeklyLessons } from '../../data/eflWeeklyLessons';
import { frenchFLSWeeklyLessons } from '../../data/frenchFLSWeeklyLessons';
import { frenchScienceWeeklyLessons } from '../../data/frenchScienceWeeklyLessons';
import { frenchSocialScienceWeeklyLessons } from '../../data/frenchSocialScienceWeeklyLessons';

// Map department names (from DB) to subject config
const SUBJECT_MAP = {
  // Senior Secondary (Years 11-13)
  'Computer Studies': { key: 'ict', label: 'Computer Studies / ICT', yearLevels: [11, 12, 13] },
  'Computer Studies (Year 11)': { key: 'ict', label: 'Computer Studies / ICT', yearLevels: [11] },
  'Biology': { key: 'biology', label: 'Biology', yearLevels: [11, 12, 13] },
  'Chemistry': { key: 'chemistry', label: 'Chemistry', yearLevels: [11, 12, 13] },
  'Physics': { key: 'physics', label: 'Physics', yearLevels: [11, 12, 13] },
  'Mathematics': { key: 'mathematics', label: 'Mathematics', yearLevels: [7, 8, 9, 10, 11, 12, 13] },
  'Agriculture': { key: 'agriculture', label: 'Agriculture', yearLevels: [11, 12, 13] },
  'Junior Agriculture': { key: 'agriculture', label: 'Agriculture', yearLevels: [7, 8, 9, 10] },
  'Economics': { key: 'economics', label: 'Economics', yearLevels: [11, 12, 13] },
  'Accounting': { key: 'accounting', label: 'Accounting', yearLevels: [11, 12, 13] },
  'Geography': { key: 'geography', label: 'Geography', yearLevels: [11, 12, 13] },
  'Development Studies': { key: 'development_studies', label: 'Development Studies', yearLevels: [11, 12, 13] },
  'History': { key: 'history', label: 'History', yearLevels: [11, 12, 13] },
  'English': { key: 'english', label: 'English / ESL', yearLevels: [7, 8, 9, 10, 11, 12, 13] },
  'French': { key: 'french', label: 'French', yearLevels: [7, 8, 9, 10, 11, 12, 13] },
  'Social Science': { key: 'geography', label: 'Social Science', yearLevels: [7, 8, 9, 10] },
  'Basic Science': { key: 'junior_science', label: 'Basic Science', yearLevels: [7, 8, 9, 10] },
  'Technology': { key: 'ict', label: 'Technology / ICT', yearLevels: [7, 8, 9, 10] },
  'Religious Education': { key: 'religious_education', label: 'Religious Education', yearLevels: [7, 8, 9, 10, 11, 12, 13] },
  'Languages': { key: 'french', label: 'Languages', yearLevels: [7, 8, 9, 10] },
  // French names (College de Tagaga)
  'Mathématiques': { key: 'french_mathematics', label: 'Mathématiques', yearLevels: [7, 8, 9, 10] },
  'Anglais': { key: 'efl', label: 'Anglais / English (EFL)', yearLevels: [7, 8, 9, 10] },
  'Sciences sociales': { key: 'french_social_science', label: 'Sciences sociales', yearLevels: [7, 8, 9, 10] },
  'Français': { key: 'fls', label: 'Français Langue Seconde', yearLevels: [7, 8, 9, 10] },
  'Science': { key: 'french_science', label: 'Science', yearLevels: [7, 8, 9, 10] },
};

// Get weekly data from curriculum files
function getWeeklyData(subjectKey) {
  const dataMap = {
    'biology': biologyWeeklyLessons,
    'chemistry': chemistryWeeklyLessons,
    'physics': physicsWeeklyLessons,
    'mathematics': mathematicsWeeklyLessons,
    'agriculture': agricultureWeeklyLessons,
    'economics': economicsWeeklyLessons,
    'accounting': accountingWeeklyLessons,
    'geography': geographyWeeklyLessons,
    'development_studies': developmentStudiesWeeklyLessons,
    'english': eslWeeklyLessons,
    'french': frenchWeeklyLessons,
    'history': historyWeeklyLessons,
    'junior_science': juniorScienceWeeklyLessons,
    'junior_mathematics': juniorMathematicsWeeklyLessons,
    'french_mathematics': frenchMathematicsWeeklyLessons,
    'efl': eflWeeklyLessons,
    'fls': frenchFLSWeeklyLessons,
    'french_science': frenchScienceWeeklyLessons,
    'french_social_science': frenchSocialScienceWeeklyLessons,
  };
  return dataMap[subjectKey] || null;
}

/**
 * Extract weekly topics from curriculum data for a given year level
 * Returns [{id, term, week, topic, strand, unit}] format for batch generator
 */
function extractWeeklyTopics(weeklyData, yearLevel, subjectKey) {
  if (!weeklyData) return [];

  const topics = [];
  // Determine which year data to use
  let yearData = null;

  // Check different data structures
  if (weeklyData[`year${yearLevel}`]) {
    yearData = weeklyData[`year${yearLevel}`];
  } else if (yearLevel >= 11 && weeklyData.year11) {
    yearData = weeklyData.year11;
  } else if (yearLevel >= 7 && yearLevel <= 10 && weeklyData.year7) {
    yearData = weeklyData.year7;
  } else if (weeklyData.term1 || weeklyData.terms) {
    yearData = weeklyData;
  }

  if (!yearData) return [];

  // Extract terms
  const termKeys = ['term1', 'term2', 'term3', 'term4'];
  for (let t = 0; t < termKeys.length; t++) {
    const termKey = termKeys[t];
    const termData = yearData[termKey];
    if (!termData) continue;

    const termLabel = `Term ${t + 1}`;
    // Handle both formats: weeks array (standard) and scheme array (ICT)
    let weeks = termData.scheme || termData.weeks || termData;
    // If weeks is a number (not an array), skip it
    if (typeof weeks === 'number') weeks = termData.scheme || termData;

    if (Array.isArray(weeks)) {
      for (const w of weeks) {
        const weekNum = w.week || w.weekNumber;
        if (!weekNum) continue;
        topics.push({
          id: `${subjectKey}-y${yearLevel}-t${t+1}-w${weekNum}`,
          term: termLabel,
          week: `Week ${weekNum}`,
          topic: `${w.topic || w.title || 'Topic'} - ${(w.learningOutcomes || w.objectives || []).slice(0, 2).join(', ')}`,
          strand: w.strand || w.unit || '',
          unit: w.unit || w.strand || termLabel,
        });
      }
    }
  }

  return topics;
}

/**
 * Get the subject config for a teacher based on their department
 */
export function getTeacherSubjects(department) {
  if (!department) return [];

  const subjects = [];
  const deptParts = department.split(',').map(d => d.trim());

  for (const part of deptParts) {
    // Direct match
    if (SUBJECT_MAP[part]) {
      subjects.push({ ...SUBJECT_MAP[part], deptName: part });
      continue;
    }
    // Fuzzy match - check if any key is contained in the dept string
    for (const [key, val] of Object.entries(SUBJECT_MAP)) {
      if (part.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(part.toLowerCase())) {
        subjects.push({ ...val, deptName: part });
        break;
      }
    }
  }

  // Deduplicate by key
  const seen = new Set();
  return subjects.filter(s => {
    if (seen.has(s.key)) return false;
    seen.add(s.key);
    return true;
  });
}

/**
 * Get weekly topics for batch generator
 */
export function getWeeklyTopicsForSubject(subjectKey, yearLevel) {
  // ICT subjects use dedicated weekly lesson files
  if (subjectKey === 'ict') {
    if (yearLevel === 11) return extractWeeklyTopics(year11ICTWeeklyLessons, yearLevel, subjectKey);
    if (yearLevel === 12) return extractWeeklyTopics(year12ICTWeeklyLessons, yearLevel, subjectKey);
    if (yearLevel === 13) return extractWeeklyTopics(year13ICTWeeklyLessons, yearLevel, subjectKey);
    return null;
  }

  const weeklyData = getWeeklyData(subjectKey);
  return extractWeeklyTopics(weeklyData, yearLevel, subjectKey);
}

export { SUBJECT_MAP };
